import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import type {
  BoundaryGroupDemographic, DemographicIdCode,
} from '~/store/boundaryGroups/boundaryGroups.repository';
import {
  type DemographicRegion,
  type DemographicsDataProvider, DemographicsDataProviders,
} from '~/store/demographics/demographics.types';
import { sharedMapUsersSelector } from '~/store/mapInfo/mapInfo.selectors';
import { isMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import {
  clientMembersSelector, userDataActiveAddonsSelector,
} from '~/store/userData/userData.selectors';
import {
  AgsDataPackagesCodes, type SubscriptionAddonCode,
} from '~/subscription/addons/addons.types';
import { type AppState } from '../../app.store';

export const demographicsListPremiumIsLoadingSelector = (state: AppState) => state.demographics.listPremium.isLoading;
export const useDemographicsListPremiumIsLoadingSelector = () => useSelector(demographicsListPremiumIsLoadingSelector);

export const demographicsListPremiumIsLoadedSelector = (state: AppState) => state.demographics.listPremium.isDataFetched;
export const useDemographicsListPremiumIsLoadedSelector = () => useSelector(demographicsListPremiumIsLoadedSelector);

export const demographicsListPremiumRegionsSelector = (state: AppState) => state.demographics.listPremium;
export const useDemographicsListPremiumRegionSelector = (provider: DemographicsDataProvider) =>
  useSelector(demographicsListPremiumRegionsSelector)[provider];

export const allAgsDemographicIdsPerRegionSelector = createStateSelector([
  demographicsListPremiumRegionsSelector,
], (demographicsData): Readonly<Partial<Record<DemographicRegion, ReadonlySet<string>>>> => {
  const agsData = demographicsData[DemographicsDataProviders.AGS];

  return agsData.reduce(
    (demographicsPerRegion, region) => {
      demographicsPerRegion[region.id] = new Set(region.demographics.map(demographic => demographic.id));
      return demographicsPerRegion;
    }, {} as Record<DemographicRegion, ReadonlySet<string>>
  );
});
export const useAllAgsDemographicIdsPerRegionSelector = () => useSelector(allAgsDemographicIdsPerRegionSelector);

const activePremiumDemographicsPackagesSelector = createStateSelector([
  userDataActiveAddonsSelector,
  clientMembersSelector,
  sharedMapUsersSelector,
], (selectedAddons, clientMembers, sharedMapUsers): Readonly<{
  purchased: ReadonlySet<SubscriptionAddonCode>;
  accessible: ReadonlySet<SubscriptionAddonCode>;
}> => {
  const agsPackageCodes: string[] = Object.values(AgsDataPackagesCodes);
  const purchased = new Set(selectedAddons.filter(addon => agsPackageCodes.includes(addon)));

  const accessible = new Set([
    ...purchased,
    ...clientMembers.flatMap(member => sharedMapUsers?.find(user => user.id === member.id)
      ? member.addons
      : []
    ),
  ]);

  return {
    purchased,
    accessible,
  };
});
export const useActivePremiumDemographicsPackagesSelector = () =>
  useSelector(activePremiumDemographicsPackagesSelector);

const filterDemographicsByPackages = (
  allDemographics: ReadonlyArray<BoundaryGroupDemographic>,
  packages: ReadonlySet<SubscriptionAddonCode>
): ReadonlySet<DemographicIdCode> => new Set(
  packages.size
    ? allDemographics
      .filter(d => (d.isTrial === true || d.package?.some(p => packages.has(p))) ?? false)
      .map(d => d.id)
    : allDemographics
      .filter(d => d.isTrial === true)
      .map(d => d.id)
);

const activePremiumDemographicsSelector = createStateSelector([
  demographicsListPremiumRegionsSelector,
  activePremiumDemographicsPackagesSelector,
  isMapPresentationalSelector,
], (demographicsPerRegion, selectedPackages, isMapPresentational): {
  [provider in DemographicsDataProvider]: Readonly<{
    purchased: ReadonlySet<DemographicIdCode>;
    accessible: ReadonlySet<DemographicIdCode>;
  }>;
} => {
  return Object.values(DemographicsDataProviders).reduce(
    (acc, provider) => {
      const allDemographics = demographicsPerRegion[provider].flatMap(region => region.demographics);

      if (isMapPresentational) {
        acc[provider] = { purchased: new Set(), accessible: new Set() };
        return acc;
      }

      acc[provider] = {
        purchased: filterDemographicsByPackages(allDemographics, selectedPackages.purchased),
        accessible: filterDemographicsByPackages(allDemographics, selectedPackages.accessible),
      };
      return acc;
    },
    {} as { [provider in DemographicsDataProvider]: Readonly<{
      purchased: ReadonlySet<DemographicIdCode>;
      accessible: ReadonlySet<DemographicIdCode>;
    }> }
  );
});
export const useActivePremiumDemographicsSelector = (provider: DemographicsDataProvider) =>
  useSelector(activePremiumDemographicsSelector)[provider];

export const demographicsListPremiumActiveSelector = createStateSelector([
  demographicsListPremiumRegionsSelector,
  activePremiumDemographicsSelector,
], (premiumStatsState, activePerProvider): Readonly<{
  purchased: ReadonlyArray<BoundaryGroupDemographic>;
  accessible: ReadonlyArray<BoundaryGroupDemographic>;
}> => {
  const filterByVariant = (variant: 'purchased' | 'accessible') =>
    Object.values(DemographicsDataProviders)
      .map(provider =>
        (premiumStatsState[provider]?.flatMap(region => region.demographics) ?? [])
          .filter(d => activePerProvider[provider][variant].has(d.id))
      )
      .flat();

  return {
    purchased: filterByVariant('purchased'),
    accessible: filterByVariant('accessible'),
  };
});

export const activePremiumDemographicsAllSelector = createStateSelector([
  demographicsListPremiumActiveSelector,
], (activePremiumDemographics): Readonly<{
  purchased: ReadonlySet<DemographicIdCode>;
  accessible: ReadonlySet<DemographicIdCode>;
}> => ({
  purchased: new Set(activePremiumDemographics.purchased.map(d => d.id)),
  accessible: new Set(activePremiumDemographics.accessible.map(d => d.id)),
}));
export const useActivePremiumDemographicsAllSelector = () =>
  useSelector(activePremiumDemographicsAllSelector);
